import { TFunction } from 'i18next'
import _ from 'lodash'
import { isObject, isUndefined, omit, remove, some } from 'lodash-es'
import {
  Participant,
  PersonType,
  Proving,
  ProvingPermission,
  ProvingUserRole,
} from 'model'
import { IDropdownOption, IGroup } from 'office-ui-fabric-react'
import * as omitDeepFn from 'omit-deep'
import shortid from 'shortid'

export const dbg = <T = any>(value: T, name?: string): T => {
  if (typeof name !== 'undefined') {
    // tslint:disable-next-line:no-console
    console.log(`[debug: ${name}]`, value)
    return value
  }

  // tslint:disable-next-line:no-console
  console.log(`[debug]`, value)
  return value as any
}

export function invokeAll<
  T extends (...args: TArgs[]) => void = any,
  TArgs = any
>(...fns: (T | undefined | null)[]) {
  return (...args: TArgs[]) => fns.forEach((fn) => fn && fn(...args))
}

export function filterPickerItems<T>(
  items: T[],
  filterFn?: (item: T) => boolean,
  selectedItems?: T[],
  keySelector?: keyof T | ((item: T) => any)
) {
  let result = [...items]
  if (filterFn) {
    result = result.filter(filterFn)
  }

  if (selectedItems && keySelector) {
    const keySelectorFn: any =
      typeof keySelector === 'string' ? (x: T) => x[keySelector] : keySelector

    const selectedKeys = selectedItems.map(keySelectorFn)

    remove(result, (item) => selectedKeys.indexOf(keySelectorFn(item)) !== -1)
  }

  return result
}

export const omitDeep: typeof omit = omitDeepFn

const mailRegEx = new RegExp(
  // eslint-disable-next-line
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
)

export const isValidMailAddress = (address: string) => {
  return mailRegEx.test(address)
}

export function getDropdownOptionsForEnum<TEnum>(
  t: TFunction,
  enumObj: any,
  enumName: string,
  emptyOption?: boolean
): IDropdownOption[] {
  const types: TEnum[] = Object.keys(enumObj).map((k) => enumObj[k])
  const result = [] as IDropdownOption[]
  types.forEach((type: TEnum) => {
    const translatedType = t(`enum.${enumName}.${type}`)
    result.push({
      key: type as unknown as string,
      text: translatedType,
    })
  })
  return emptyOption ? [{ key: '', text: '' }, ...result] : result
}

export const clearObjectIdsByPath = (
  value: any,
  updatePaths?: string[]
): any => {
  if (updatePaths && updatePaths.length > 0) {
    updatePaths.forEach((path: string) => {
      const pathvalue = _.get(value, path)

      if (pathvalue !== undefined) {
        if (typeof pathvalue === 'string' || pathvalue instanceof String) {
          const idvalue = _.get(value, path)
          if (idvalue === '') {
            _.set(value, path, null)
          }
        }
      }
    })
  }

  return value
}

export const removeNullProperties = (obj: any): any => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] == null) {
      delete obj[key]
    }

    if (typeof obj[key] === 'object') {
      removeNullProperties(obj[key])
    }
  })

  return obj
}

export function omitDeepRegex(input: any, regex: RegExp): any {
  function omitDeepOnOwnProps(obj: any): any {
    if (isUndefined(obj)) {
      return obj
    }

    if (!Array.isArray(obj) && !isObject(obj)) {
      return obj
    }

    if (Array.isArray(obj)) {
      return omitDeepRegex(obj, regex)
    }

    const o = {}
    const props = []
    for (const [key, value] of Object.entries(obj)) {
      o[key] = !isNullOrUndefined(value) ? omitDeepRegex(value, regex) : value
      if (regex.test(key)) {
        props.push(key)
      }
    }

    return omit(o, props)
  }

  if (Array.isArray(input)) {
    return input.map(omitDeepOnOwnProps)
  }

  return omitDeepOnOwnProps(input)
}

export function isNullOrUndefined(value: any) {
  return value === null || value === undefined
}

export function hasProvingPermission(
  proving: Proving,
  ...permissions: ProvingPermission[]
) {
  return some(
    permissions,
    (permission) =>
      proving &&
      proving.authorization &&
      proving.authorization.permissions.indexOf(permission) !== -1 &&
      proving.status !== 8 &&
      proving.authorization.permissions.indexOf(permission) !== -1 &&
      proving.status !== 9
  )
}
export function canReadProving(proving: Proving) {
  return hasProvingPermission(proving, ProvingPermission.Read)
}
export function canUpdateProving(proving: Proving) {
  return hasProvingPermission(proving, ProvingPermission.Update)
}
export function canDeleteProving(proving: Proving) {
  return hasProvingPermission(proving, ProvingPermission.Delete)
}

export function isInProvingRole(proving: Proving, role: ProvingUserRole) {
  return proving.authorization && proving.authorization.role === role
}

export function generateNestedGroups<T>(
  itemsInput: T[],
  groupingCallbacksInput: any[]
) {
  function create_UUID() {
    return shortid.generate()
  }
  const sortAlphaNum = (a: IGroup, b: IGroup) =>
    a.name.localeCompare(b.name, 'en', { numeric: true })
  const groupByNested = (items: T[], groupCallbacks: any[]) => {
    const grouped = items.reduce((r, dataElement) => {
      // @ts-ignore
      const pr = groupCallbacks.reduce(
        // @ts-ignore
        (group: any, key: any, i: any, { length }) => {
          const keyValue = key(dataElement)
          group[keyValue] = group[keyValue] || (i + 1 === length ? [] : {})
          return group[keyValue]
        },
        r
      )
      pr.push(dataElement)

      return r
    }, {})
    return grouped
  }
  const getNestedGroups = (
    currentPartition: any,
    maxLevel: number,
    name = '',
    level = -1,
    startIndex = 0
  ) => {
    let items: T[] = []
    const childrenGroups: IGroup[] = []
    if (Array.isArray(currentPartition)) {
      items.push(...currentPartition)
    } else {
      Object.keys(currentPartition).forEach((subgroup) => {
        const [group, subItems] = getNestedGroups(
          currentPartition[subgroup],
          maxLevel,
          subgroup,
          level + 1,
          startIndex + items.length
        )
        childrenGroups.push(group as IGroup)
        // @ts-ignore
        items = [...items, ...subItems]
      })
    }
    // sort groups on the same level Alphanumerically
    childrenGroups.sort(sortAlphaNum)
    if (level === -1) {
      return [childrenGroups, items]
    } else {
      const group = {
        key: create_UUID(),
        name,
        startIndex,
        count: items.length,
        level,
        isCollapsed: true,
        children: childrenGroups,
      }
      return [group, items]
    }
  }
  const groupedObject = groupByNested(itemsInput, groupingCallbacksInput)
  const groupsAndItems = getNestedGroups(
    groupedObject,
    groupingCallbacksInput.length
  )
  return groupsAndItems
}

export const getParticipantDisplayName = (participant: Participant) => {
  if (participant.person) {
    return `${participant.person.lastName}, ${participant.person.firstName}`
  } else if (
    participant.unknownPerson &&
    participant.unknownPerson.isUnknown &&
    participant.unknownPerson.personType === PersonType.Supplier
  ) {
    if (
      participant.unknownPerson.surName &&
      participant.unknownPerson.firstName &&
      participant.unknownPerson.supplier
    ) {
      return `${participant.unknownPerson.surName}, ${participant.unknownPerson.firstName} - ${participant.unknownPerson.supplier}`
    } else {
      return 'Teilnehmer unbekannt'
    }
  } else if (
    participant.unknownPerson &&
    participant.unknownPerson.isUnknown &&
    (participant.unknownPerson.personType === PersonType.User ||
      participant.unknownPerson.personType === PersonType.External ||
      participant.unknownPerson.personType === PersonType.Supplier)
  ) {
    return 'Teilnehmer unbekannt'
  } else if (
    participant.unknownPerson &&
    !participant.unknownPerson.isUnknown &&
    (participant.unknownPerson.personType === PersonType.User ||
      participant.unknownPerson.personType === PersonType.External)
  ) {
    return `${participant.unknownPerson.surName}, ${participant.unknownPerson.firstName}`
  } else if (
    participant.unknownPerson &&
    participant.unknownPerson.isUnknown &&
    participant.unknownPerson.personType === PersonType.Worker
  ) {
    return participant.percentage + '%, ' + participant.unknownPerson.department
  }
  return ''
}
